import { Button } from "@etudescarre/ui";
import { isNotNil, validatePasswordStrength } from "@etudescarre/utils";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { PasswordCriteria } from "./password-criteria";

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePassword() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ChangePasswordFormData>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const { errors } = form.formState;

  const handleSubmit = async (data: ChangePasswordFormData) => {
    setError("");
    setIsLoading(true);

    try {
      const result = await authClient.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        revokeOtherSessions: true,
      });

      if (result.error) {
        // Better Auth answers INVALID_PASSWORD only when the current password
        // does not match; the new one was already checked above.
        if (result.error.code === "INVALID_PASSWORD") {
          form.setError(
            "currentPassword",
            {
              message:
                "Mot de passe actuel incorrect. S'il a été rempli automatiquement par votre navigateur, saisissez-le vous-même.",
            },
            { shouldFocus: true },
          );
        } else if (result.error.status === 429) {
          setError(
            "Trop de tentatives. Veuillez réessayer dans quelques instants.",
          );
        } else {
          setError(
            "Le mot de passe n'a pas pu être modifié. Veuillez réessayer.",
          );
        }
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="mx-auto max-w-md py-12">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="mb-6 font-semibold text-2xl text-gray-900">
            Mot de passe modifié
          </h1>
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-green-800 text-sm">
                Votre mot de passe a été modifié avec succès.
              </p>
            </div>
            <p className="text-gray-500 text-sm">
              Redirection vers l'accueil...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md py-12">
      <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="mb-6 font-semibold text-2xl text-gray-900">
          Modifier le mot de passe
        </h1>

        <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
          <div>
            <label
              className="mb-2 block font-medium text-gray-700 text-sm"
              htmlFor="currentPassword"
            >
              Mot de passe actuel
            </label>
            <input
              {...form.register("currentPassword")}
              aria-describedby="currentPassword-error"
              aria-invalid={isNotNil(errors.currentPassword)}
              autoComplete="current-password"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
              id="currentPassword"
              placeholder="Entrez votre mot de passe actuel"
              required
              type="password"
            />
            {errors.currentPassword && (
              <p
                className="mt-1 text-red-600 text-sm"
                id="currentPassword-error"
              >
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div>
            <label
              className="mb-2 block font-medium text-gray-700 text-sm"
              htmlFor="newPassword"
            >
              Nouveau mot de passe
            </label>
            <input
              {...form.register("newPassword", {
                validate: (val) => validatePasswordStrength(val).length === 0,
              })}
              aria-describedby="newPassword-criteria"
              aria-invalid={isNotNil(errors.newPassword)}
              autoComplete="new-password"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
              id="newPassword"
              placeholder="Votre nouveau mot de passe"
              required
              type="password"
            />
            <PasswordCriteria
              id="newPassword-criteria"
              password={form.watch("newPassword")}
              showErrors={isNotNil(errors.newPassword)}
            />
          </div>

          <div>
            <label
              className="mb-2 block font-medium text-gray-700 text-sm"
              htmlFor="confirmPassword"
            >
              Confirmer le nouveau mot de passe
            </label>
            <input
              {...form.register("confirmPassword", {
                validate: (val) =>
                  val === form.getValues("newPassword") ||
                  "Les mots de passe ne correspondent pas.",
              })}
              aria-describedby="confirmPassword-error"
              aria-invalid={isNotNil(errors.confirmPassword)}
              autoComplete="new-password"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
              id="confirmPassword"
              placeholder="Confirmez le mot de passe"
              required
              type="password"
            />
            {errors.confirmPassword && (
              <p
                className="mt-1 text-red-600 text-sm"
                id="confirmPassword-error"
              >
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Link className="flex-1" to="/">
              <Button className="w-full" outline type="button">
                Annuler
              </Button>
            </Link>
            <Button
              className="flex-1 bg-[#1a3b5c] hover:bg-[#2a4b6c]"
              disabled={isLoading}
              type="submit"
            >
              {isLoading ? "Modification..." : "Modifier"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;
